import { Button } from "@/components/ui/button.tsx"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.tsx"
import { Textarea } from "@/components/ui/textarea.tsx"
import { formatDistanceToNow } from "date-fns"
import { Dot, NotebookPen } from "lucide-react"
import { useState } from "preact/hooks"
import { api_fetch_throws } from "@/api/client.tsx"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { DeductionListJson, DeductionNote, save_note } from "@/api"
import { useAsyncEffect } from "@/utils"

interface NoteContentProps {
  note: DeductionNote
}

function NoteContent({ note }: NoteContentProps) {
  return (
    <div className="flex flex-col mb-2">
      <div className="mt-2 mb-1 rounded-tl-lg rounded-tr-lg rounded-br-lg bg-plue-100 p-2 text-sm text-plue-700">
        {note.message}
      </div>
      <div className="flex items-center text-sm">
        <span className="text-gray-900">{note.actor_name}</span>
        <Dot className="h-2 w-2 mx-1" />
        <span>{formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}</span>
      </div>
    </div>
  )
}

interface NotesDialogContentProps {
  deduction: DeductionListJson
}

export function ListAddNoteButton({ deduction }: NotesDialogContentProps) {
  const [note, setNote] = useState("")
  const [notes, setNotes] = useState<DeductionNote[]>([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const statusFlag = RefreshFlag.use(sf => sf!)
  let [loading, setLoading] = useState(false)

  useAsyncEffect(async () => {
    if (!isDialogOpen) {
      return
    }
    let res = await api_fetch_throws<DeductionNote[]>(`/deduction/${deduction.id}/notes`)
    setLoading(false)
    setNotes(res)
  }, [isDialogOpen, statusFlag])

  async function handle_save_note() {
    if (!note) {
      return
    }
    setLoading(true)
    await save_note(note, deduction.id)
    setNote("")
    RefreshFlag.set(flag => !flag)
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild={true}>
        {deduction.has_notes ? (
          <NotebookPen className="h-5 w-5 cursor-pointer text-gray-900" />
        ) : (
          <NotebookPen className="h-5 w-5 cursor-pointer text-gray-400" />
        )}
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle className="mb-2">Notes</DialogTitle>
          <DialogDescription asChild>
            <div className="overflow-y-auto max-h-96">
              {notes.map(note => (
                <NoteContent note={note} key={note.id} />
              ))}
            </div>
          </DialogDescription>
        </DialogHeader>
        <Textarea
          // @ts-ignore
          value={note}
          onChange={(e: any) => setNote(e.target.value)}
          onKeyDown={(e: any) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
              e.preventDefault()
              handle_save_note()
            }
          }}
        />
        <Button variant="tertiary" disabled={loading} onClick={handle_save_note}>
          Save
        </Button>
      </DialogContent>
    </Dialog>
  )
}
